<template>

  <group-card title="Spouses Details & Employment" icon="wc" :disabled="is_secondary_applicant" :tooltipText="tooltipText">
    <template slot="actions">
      <md-checkbox v-model="has_spouse_as_applicant">Make Applicant</md-checkbox>
    </template>
    <div class="boxed-category">
    <md-subheader>Spouses Name</md-subheader>
    <div class="md-layout input-layout">

      <!-- First Name -->
      <validated-text-input class="md-layout-item md-small-size-100 md-size-15"
        label="First Name" v-model="applicant.personal_details.spouse.name.firstname" :prefixLabel="prefixLabel">
      </validated-text-input>

      <!-- Middle Name -->
      <validated-text-input class="md-layout-item md-small-size-100 md-size-15"
        label="Middle Name" v-model="applicant.personal_details.spouse.name.middlename" :prefixLabel="prefixLabel">
      </validated-text-input>

      <!-- Last Name -->
      <validated-text-input class="md-layout-item md-small-size-100 md-size-15"
        label="Last Name" v-model="applicant.personal_details.spouse.name.lastname" :prefixLabel="prefixLabel">
      </validated-text-input>

      <!-- Mobile Contact Number -->
      <validated-text-input
        class="md-layout-item md-small-size-100 md-size-15"
        label="Mobile Contact Number"
        v-model="applicant.personal_details.spouse.phone_number"
        :prefixLabel="prefixLabel"
        prefix="+"
        :minLength=7
        :maxLength=15
        errormsg="Please enter a valid phone number, format can contain 7 to 15 digits"
        melissaCheckType="phone"
      />

      <!-- Email Address -->
      <validated-text-input
        class="md-layout-item md-small-size-100 md-size-40"
        label="Email"
        v-model="applicant.personal_details.spouse.email"
        :prefixLabel="prefixLabel"
        melissaCheckType="email"
      />

    </div>
    </div>
    <div class="boxed-category">
    <md-subheader>Spouses Employment</md-subheader>
    <div class="md-layout input-layout">

      <!-- Employment Type -->
      <validated-autocomplete-input class="md-layout-item md-small-size-100 md-size-20"
        label="Type" v-model="applicant.personal_details.spouse.employment.type"
         :prefixLabel="prefixLabel"
        :options="options.employment_type_options"
        force
      />

      <!-- Employer -->
      <validated-text-input
        class="md-layout-item md-small-size-100 md-size-25"
        label="Employer"
        :prefixLabel="prefixLabel"
        v-model="applicant.personal_details.spouse.employment.employer_name">
      </validated-text-input>

      <!-- Duration Years -->
      <validated-input
        type="DURATION_YEARS"
        class="md-layout-item md-small-size-100 md-size-10"
        label="Years"
        :prefixLabel="prefixLabel"
        v-model="applicant.personal_details.spouse.employment.duration.years"
      />

      <!-- Duration Months -->
      <validated-input
        type="DURATION_MONTHS"
        class="md-layout-item md-small-size-100 md-size-10"
        label="Months"
        :prefixLabel="prefixLabel"
        v-model="applicant.personal_details.spouse.employment.duration.months"
      />

      <!-- Income Amount -->
      <validated-input
        type="CURRENCY"
        class="md-layout-item md-small-size-100 md-size-20"
        label="Net Income (After tax)"
        :prefixLabel="prefixLabel"
        prefix="$"
        v-model="applicant.personal_details.spouse.employment.income.amount"
        :suffix="toMonthlyAmount(applicant.personal_details.spouse.employment.income.amount, applicant.personal_details.spouse.employment.income.frequency, true) + '/m'"
      />

      <!-- Income Frequency -->
      <validated-autocomplete-input class="md-layout-item md-small-size-100 md-size-15"
        label="Frequency" v-model="applicant.personal_details.spouse.employment.income.frequency"
         :prefixLabel="prefixLabel"
        :options="options.payment_frequency_options"
        force
      />

    </div>
    </div>
    <md-dialog-confirm
      :md-active.sync="confirmDialog.active"
      :md-title="confirmDialog.title"
      :md-content="confirmDialog.content"
      md-confirm-text="Continue"
      md-cancel-text="Cancel"
      @md-confirm="removeSpouse" />
  </group-card>

</template>

<script>
import { mapGetters } from 'vuex';
import filters from '@/components/utils/filters';
import ValidatedInputs from '@/components/ValidatedInput';
import GroupCard from '@/components/Cards/GroupCard';
import GroupCardRow from '@/components/Cards/GroupCardRow';
import EditApplicantEmployment from "@/components/Editors/EditApplicantEmployment";
import {newEmployment, newApplicant} from "../../schema";

export default {
  components: {
    ...ValidatedInputs,
    GroupCard,
    GroupCardRow,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    application: {
      type: Object,
      required: true
    },
    prefixLabel: {
      type: String,
      default: 'Spouse'
    },
  },
  data(){
    return {
      confirmDialog:{
        title: null,
        content: null,
        onConfirm: null,
        active: false
      },
      temporarySpouseIndex: null,
      tooltipText: '<p>Advise client that we only collect these details to show that expenses are shared</p>'
    }
  },
  computed: {
    // standard list of options from store
    ...mapGetters({
      options: 'options',
    }),

    // internal reference to the v-model on this component, handles incomeing and outgoing changes
    applicant: {
      get(){ return this.value },
      set(applicant){ this.$emit('input', applicant) },
    },
    loan_application:{
      get(){return this.application;},
      set(application){ this.$emit('input', application) },
    },
    has_spouse_as_applicant: {
      get(){
        return this.applicant.personal_details.spouse.spouse_applicant_id ? true : this.applicant.personal_details.spouse_index ? true : false;
      },
      set(flag){
        if(flag) {
          this.addSpouse();
        } else {
          if (this.application.applicants.length == 1) {
            this.applicant.personal_details.spouse_index = null;
            this.applicant.personal_details.spouse.spouse_applicant_id = null;
            return;
          }
          this.showSpouseRemoveConfirmationBox("Warning!", "This will remove the applicant and all the prefilled data will be lost. Do you wish to continue?");
        }
      }
    },
    is_secondary_applicant(){
      return this.applicant.type == 'Secondary Borrower';
    }
  },
  methods: {
    // make all filters available as methods
    ...filters,
    addSpouse() {
      let spouseDetails = this.applicant.personal_details.spouse;
      let new_spouse = newApplicant();
      new_spouse.contact.name = spouseDetails.name;
      let spouseEmployment = newEmployment();
      spouseEmployment.type = spouseDetails.employment.type;
      spouseEmployment.employer_details.name = spouseDetails.employment.employer_name;
      spouseEmployment.duration = spouseDetails.employment.duration;
      spouseEmployment.income = spouseDetails.employment.income;
      new_spouse.employments.current[0] = spouseEmployment;
      // email
      new_spouse.contact.emails[0].email = spouseDetails.email;
      // mobile contact number
      new_spouse.contact.phones[0].phone_number = spouseDetails.phone_number;
      new_spouse.type = "Secondary Borrower";
      new_spouse.personal_details.spouse_index = this.application.applicants.indexOf(this.applicant);
      new_spouse.personal_details.marital_status = "Married";
      // set second applicant's spouse to be first applicant
      new_spouse.personal_details.spouse.name = this.applicant.contact.name;
      new_spouse.personal_details.spouse.employment = this.applicant.employments.current[0];
      let newArrayLength = this.application.applicants.push(new_spouse);
      this.temporarySpouseIndex = newArrayLength - 1;
      this.applicant.personal_details.spouse_index = this.temporarySpouseIndex;
      new_spouse.addresses.current = this.applicant.addresses.current;
    },
    removeSpouse() {
      let spouse_index = null;
      if(!this.temporarySpouseIndex){
        let spouse_id = this.applicant.personal_details.spouse.spouse_applicant_id;
        spouse_index = this.application.applicants.findIndex(applicant => applicant.id == spouse_id);
      } else {
        spouse_index = this.temporarySpouseIndex;
      }
      this.application.applicants.splice(spouse_index, 1);
      this.applicant.personal_details.spouse_index = null;
      this.applicant.personal_details.spouse.spouse_applicant_id = null;
    },
    showSpouseRemoveConfirmationBox(title, content) {
      this.confirmDialog.title = title;
      this.confirmDialog.content = content;
      this.confirmDialog.active = true;
    }
  },
  filters: {
    // make all filters available as filters
    ...filters,
  },
  async beforeCreate() {
    const params = {
      types: [
        'employment_type_options',
        'payment_frequency_options',
      ],
    }
    await this.$store.dispatch('fetch_types', { params });
  },
};
</script>
